import Card from '../../../components/common/Card/Card'
import Badge from '../../../components/common/Badge/Badge'
import {
  formatHolidayDate,
  getTypeBadgeVariant,
  calculateDuration
} from '../../../services/holidaysService'

/**
 * HolidayCard Component
 * Display individual holiday with admin actions
 *
 * @param {object} holiday - Holiday data
 * @param {boolean} isUpcoming - Whether holiday is upcoming
 * @param {boolean} isAdmin - Whether current user is admin
 * @param {function} onEdit - Edit callback (admin only)
 * @param {function} onDelete - Delete callback (admin only)
 */
const HolidayCard = ({ holiday, isUpcoming = false, isAdmin = false, onEdit, onDelete }) => {
  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${holiday.name}"? This action cannot be undone.`)) {
      onDelete(holiday.id)
    }
  }

  return (
    <Card
      style={{
        border: isUpcoming ? '2px solid var(--psyber-info)' : undefined,
        background: isUpcoming
          ? 'linear-gradient(135deg, rgba(97, 218, 251, 0.05) 0%, rgba(138, 123, 255, 0.05) 100%)'
          : undefined
      }}
    >
      {/* Header with Badge and Admin Actions */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: '0.75rem',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', flex: 1 }}>
          <Badge
            variant={getTypeBadgeVariant(holiday.type)}
            icon={
              holiday.type === 'National' ? 'fas fa-flag' :
              holiday.type === 'Company' ? 'fas fa-building' :
              'fas fa-star'
            }
          >
            {holiday.type}
          </Badge>
          {isUpcoming && (
            <Badge variant="info" icon="fas fa-clock">
              Upcoming
            </Badge>
          )}
        </div>

        {/* Admin Actions */}
        {isAdmin && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => onEdit(holiday)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--psyber-primary)',
                fontSize: 'var(--psyber-font-size-base)',
                cursor: 'pointer',
                padding: '0.25rem 0.5rem',
                borderRadius: 'var(--psyber-radius-sm)',
                transition: 'var(--psyber-transition-fast)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--psyber-card-hover)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none'
              }}
              title="Edit holiday"
            >
              <i className="fas fa-edit"></i>
            </button>
            <button
              onClick={handleDelete}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--psyber-error)',
                fontSize: 'var(--psyber-font-size-base)',
                cursor: 'pointer',
                padding: '0.25rem 0.5rem',
                borderRadius: 'var(--psyber-radius-sm)',
                transition: 'var(--psyber-transition-fast)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--psyber-error-bg)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none'
              }}
              title="Delete holiday"
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        )}
      </div>

      {/* Holiday Name */}
      <h3 style={{
        margin: '0 0 0.5rem 0',
        fontSize: 'var(--psyber-font-size-lg)',
        color: 'var(--psyber-text)',
        fontWeight: 600
      }}>
        {holiday.name}
      </h3>

      {/* Date and Day of Week */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        color: 'var(--psyber-text-secondary)',
        fontSize: 'var(--psyber-font-size-sm)'
      }}>
        <i className="fas fa-calendar-day"></i>
        <span>{formatHolidayDate(holiday.date, holiday.endDate, holiday.dayOfWeek)}</span>
      </div>

      {/* Duration Badge (multi-day holidays only) */}
      {holiday.endDate && holiday.endDate !== holiday.date && (
        <div style={{ marginTop: '0.5rem' }}>
          <Badge variant="info" icon="fas fa-calendar-days">
            {calculateDuration(holiday.date, holiday.endDate)} days
          </Badge>
        </div>
      )}
    </Card>
  )
}

export default HolidayCard
